// InlineSelect — a dropdown that renders its option list INSIDE the page DOM
// (not a native OS <select> pop-up, which doesn't show in screen-shares). The
// option panel is rendered through a portal to document.body and positioned
// over the trigger, so it can't be clipped by a scrolling/overflow ancestor
// (e.g. the ticket drawer). Styled to match the app (forest accents).

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';

export type InlineSelectOption = { value: string; label: string; hint?: string };

type Rect = { top: number; left: number; width: number };

export default function InlineSelect({
  value,
  onChange,
  options,
  ariaLabel,
  placeholder = 'Select…',
  className = '',
  buttonClassName = '',
}: {
  value: string;
  onChange: (value: string) => void;
  options: InlineSelectOption[];
  ariaLabel?: string;
  placeholder?: string;
  className?: string;
  buttonClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<Rect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const current = options.find((o) => o.value === value);

  function measure(): void {
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) setRect({ top: r.bottom + 4, left: r.left, width: r.width });
  }

  // Keep the portalled panel pinned to the trigger before paint + while open.
  useLayoutEffect(() => {
    if (open) measure();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent): void {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    function reposition(): void {
      measure();
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', reposition);
    // capture=true so we catch scrolling in ANY ancestor (e.g. the drawer).
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  return (
    <div className={`relative ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => {
          if (!open) measure();
          setOpen((o) => !o);
        }}
        className={`neo-input w-full flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm text-foreground text-left focus:outline-none focus:ring-2 focus:ring-forest/30 ${buttonClassName}`}
      >
        <span className="truncate">{current?.label ?? placeholder}</span>
        <ChevronDown
          className={`w-4 h-4 flex-shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open &&
        rect &&
        createPortal(
          <div
            ref={panelRef}
            role="listbox"
            className="fixed z-[60] max-h-64 overflow-y-auto rounded-lg bg-card py-1 shadow-lg"
            style={{
              top: rect.top,
              left: rect.left,
              width: rect.width,
              border: '1px solid var(--border-soft)',
            }}
          >
            {options.map((o) => {
              const selected = o.value === value;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                  className={`w-full flex items-start gap-2 px-3 py-2 text-left text-sm transition-colors ${
                    selected ? 'bg-forest-soft text-forest' : 'text-foreground hover:bg-secondary'
                  }`}
                >
                  <Check
                    className={`w-4 h-4 mt-0.5 flex-shrink-0 text-forest ${selected ? 'opacity-100' : 'opacity-0'}`}
                  />
                  <span className="min-w-0">
                    <span className="block truncate">{o.label}</span>
                    {o.hint && (
                      <span className="block text-xs text-muted-foreground">{o.hint}</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}
